import * as z from 'zod';

import { ActorRoleSchema } from '../common/enums.js';
import {
  EmailSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LabelSchema,
  ShortTextSchema,
} from '../common/primitives.js';

/**
 * access context — local email/password, JWT sessions, RBAC.
 * The refresh token lives only in an HttpOnly cookie, so it never appears in
 * any schema here; that absence is the contract.
 */

/** Password policy is enforced here so web and API cannot disagree about it. */
export const PasswordSchema = z.string().min(12).max(256);

export const UserSchema = z.strictObject({
  id: IdentifierSchema,
  email: EmailSchema,
  displayName: LabelSchema,
  roles: z.array(ActorRoleSchema).min(1),
  createdAt: IsoDateTimeSchema,
  lastLoginAt: IsoDateTimeSchema.nullable(),
});
export type User = z.infer<typeof UserSchema>;

export const SessionSchema = z.strictObject({
  accessToken: z.string().min(1),
  /** Seconds until `accessToken` expires. The web app keeps it in memory only. */
  expiresIn: z.int().min(1),
  user: UserSchema,
});
/** Named because three responses are this shape and callers hold it generically. */
export type Session = z.infer<typeof SessionSchema>;

export const RegisterRequestSchema = z.strictObject({
  email: EmailSchema,
  password: PasswordSchema,
  displayName: LabelSchema,
  /** First user bootstraps as ADMIN; later self-registration lands on VIEWER. */
  invitationCode: ShortTextSchema.optional(),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;
export const RegisterResponseSchema = SessionSchema;
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

export const LoginRequestSchema = z.strictObject({
  email: EmailSchema,
  password: PasswordSchema,
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export const LoginResponseSchema = SessionSchema;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/** Body is empty: the rotating refresh token travels as a cookie. */
export const RefreshRequestSchema = z.strictObject({});
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;
export const RefreshResponseSchema = SessionSchema;
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

export const LogoutRequestSchema = z.strictObject({
  /** Revoke every session of this user, not just the current device. */
  allSessions: z.boolean().optional(),
});
export type LogoutRequest = z.infer<typeof LogoutRequestSchema>;
export const LogoutResponseSchema = z.strictObject({
  revokedAt: IsoDateTimeSchema,
  revokedSessions: z.int().min(0),
});
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;

export const MeRequestSchema = z.strictObject({});
export type MeRequest = z.infer<typeof MeRequestSchema>;
export const MeResponseSchema = z.strictObject({
  user: UserSchema,
  /** Effective permissions are derived from roles server-side; the UI mirrors them. */
  roles: z.array(ActorRoleSchema).min(1),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

// ---------------------------------------------------------------------------
// Federated sign-in (docs/architecture/04-W3ID-SSO.md)
//
// The two OIDC endpoints themselves are browser redirects and deliberately not
// operations in this manifest (§8 of that document). What the SPA needs from the
// contract is only whether to render the button, which is a JSON question.
// ---------------------------------------------------------------------------

/** The external providers this platform can be configured to federate with. */
export const AUTH_PROVIDER_IDS = ['w3id'] as const;
export const AuthProviderIdSchema = z.enum(AUTH_PROVIDER_IDS);
export type AuthProviderId = z.infer<typeof AuthProviderIdSchema>;

/**
 * Why a federated sign-in did not end in a session. The callback is a navigation, so it
 * cannot answer with an error envelope: it redirects to `/login?sso=<reason>` and the login
 * screen turns the reason into copy.
 *
 * Shared here so the API's redirect and the web app's copy table cannot drift apart.
 *
 * The set is deliberately COARSE. In particular there is no reason that distinguishes
 * "suspended" from "unknown" — `denied` covers an account that exists and may not
 * authenticate, and it is also what an administrator sees, because a reason that separated
 * the two would turn the login screen into a directory oracle.
 */
export const SSO_FAILURE_REASONS = [
  /** The IdP returned `error=access_denied` — the person declined, or policy refused. */
  'cancelled',
  /** No flow row: missing, expired, or already consumed. Includes an honest double-submit. */
  'expired',
  /** The flow cookie was absent or did not match the row. Browser binding failed. */
  'mismatch',
  /** Token exchange, ID-token verification, or claim mapping failed. */
  'failed',
  /** An unknown person, and self-registration is off. An admin must create the account. */
  'not_provisioned',
  /** The account exists and cannot authenticate. */
  'denied',
] as const;
export const SsoFailureReasonSchema = z.enum(SSO_FAILURE_REASONS);
export type SsoFailureReason = z.infer<typeof SsoFailureReasonSchema>;

export const AuthProvidersRequestSchema = z.strictObject({});
export type AuthProvidersRequest = z.infer<typeof AuthProvidersRequestSchema>;

/**
 * One configurable sign-in provider. Note what is absent and must stay absent: the issuer
 * url, the client id, and anything else that is configuration. This response answers "is
 * there a button, what does it say, where does it go" and nothing else — it is public, so
 * every field here is a field an anonymous caller learns.
 */
export const AuthProviderSchema = z.strictObject({
  id: AuthProviderIdSchema,
  /** Button label. Fixed server-side; never composed from configuration. */
  displayName: LabelSchema,
  /** False when the deployment has no OIDC configuration. The button is not rendered. */
  enabled: z.boolean(),
  /**
   * The same-origin path to navigate to. A path and not a URL: the browser must reach this
   * server first, so there is nothing here that could send anyone off-origin.
   */
  startPath: z.string().regex(/^\/[A-Za-z0-9\-._~/]*$/, 'must be a same-origin absolute path'),
});
export type AuthProvider = z.infer<typeof AuthProviderSchema>;

export const AuthProvidersResponseSchema = z.strictObject({
  /** Empty when nothing is configured — the web app renders the password form alone. */
  providers: z.array(AuthProviderSchema),
});
export type AuthProvidersResponse = z.infer<typeof AuthProvidersResponseSchema>;
